import { forwardRef, ReactNode } from "react";

import MuiPopper from "@mui/material/Popper";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import MuiTooltip, { TooltipProps as MuiTooltipProps } from "@mui/material/Tooltip";

import { mergeSx } from "@/helpers/mergeSx";

export type DashboardTooltipVibe = "brand" | "neutral" | "positive" | "warning" | "reverse";

export type DashboardTooltipProps = Omit<
  MuiTooltipProps,
  "classes" | "color" | "title" | "PopperComponent" | "arrow"
> & {
  title?: ReactNode;
  vibe?: DashboardTooltipVibe;
  disabled?: boolean;
  slotProps?: {
    tooltip?: {
      sx?: SxProps<Theme>;
    };
    arrow?: {
      sx?: SxProps<Theme>;
    };
  };
  "data-test"?: string;
};

export const DashboardTooltip = forwardRef<HTMLDivElement, DashboardTooltipProps>(
  ({ vibe = "neutral", title, disabled = false, children, slotProps, "data-test": dataTest, ...rest }, ref) => {
    const theme = useTheme();

    if (!title || disabled) {
      return <>{children}</>;
    }

    const tooltipStyles = mergeSx(
      {
        padding: theme.spacing(0.75, 1),
        ...theme.typography.body2,
        textAlign: "center",
        textWrap: "balance",
        ...(vibe === "brand" && {
          backgroundColor: theme.palette.surface.brand.intense,
          color: theme.palette.text.reverse.intense,
          boxShadow: theme.boxShadow.palette(theme.palette.surface.brand.intense).mid,
        }),
        ...(vibe === "neutral" && {
          backgroundColor: theme.palette.surface.neutral.intense,
          color: theme.palette.text.reverse.intense,
          boxShadow: theme.boxShadow.palette(theme.palette.surface.neutral.intense).mid,
        }),
        ...(vibe === "positive" && {
          backgroundColor: theme.palette.surface.positive.intense,
          color: theme.palette.text.reverse.intense,
          boxShadow: theme.boxShadow.palette(theme.palette.surface.positive.intense).mid,
        }),
        ...(vibe === "warning" && {
          backgroundColor: theme.palette.surface.warning.intense,
          color: theme.palette.text.reverse.intense,
          boxShadow: theme.boxShadow.palette(theme.palette.surface.warning.intense).mid,
        }),
        ...(vibe === "reverse" && {
          backgroundColor: theme.palette.surface.neutral.white,
          color: theme.palette.text.neutral.intense,
          border: `1px solid ${theme.palette.border.neutral.quiet}`,
        }),
      },
      slotProps?.tooltip?.sx
    );

    const arrowStyles = mergeSx(
      {
        ...(vibe === "brand" && {
          color: theme.palette.surface.brand.intense,
        }),
        ...(vibe === "neutral" && {
          color: theme.palette.surface.neutral.intense,
        }),
        ...(vibe === "positive" && {
          color: theme.palette.surface.positive.intense,
        }),
        ...(vibe === "warning" && {
          color: theme.palette.surface.warning.intense,
        }),
        ...(vibe === "reverse" && {
          color: theme.palette.surface.neutral.white,
        }),
        ...(vibe === "reverse" && {
          ["&:before"]: {
            border: `1px solid ${theme.palette.border.neutral.quiet}`,
          },
        }),
      },
      slotProps?.arrow?.sx
    );

    return (
      <MuiTooltip
        ref={ref}
        {...rest}
        title={title}
        arrow
        enterTouchDelay={0}
        leaveTouchDelay={10000}
        PopperComponent={(props) => <MuiPopper {...props} data-test={dataTest} />}
        slotProps={{
          tooltip: {
            sx: tooltipStyles,
          },
          arrow: {
            sx: arrowStyles,
          },
        }}
      >
        {children}
      </MuiTooltip>
    );
  }
);
DashboardTooltip.displayName = "DashboardTooltip";
